/**
 * MyRootsDemo — "Ancestral Constellation" cinematic redesign (Apr 30 2026).
 *
 * Replaces the white-card org-chart with a deep-black draggable
 * constellation per the design blueprint:
 *   - Sticky glass header with gold serif title + holographic CTAs
 *   - 80vh constellation canvas (draggable pan via Framer Motion)
 *   - Generations are massive faint serif watermarks (Era Bands) sitting
 *     at z-0 behind the orbs
 *   - Each ancestor is a glowing circular orb (no card chrome) with the
 *     name floating beside; hover expands a glass tooltip with metadata
 *   - Connecting filaments are SVG gold paths that draw themselves on
 *     mount (Framer Motion pathLength)
 *   - Empty/demo seed pulses concentric rings
 *
 * All test IDs preserved from the previous implementation. Blueprint
 * test IDs added: roots-page, roots-tree, roots-add-member,
 * roots-import-gedcom, roots-node.
 */
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowRight, Sparkles, MapPin, Calendar, Users, Compass, Move } from 'lucide-react';

// ─── Mock family — same structure as before ───────────────────────
const TREE = {
  grandparents: [
    { id: 'gp-1', name: 'Eleanor Marsh',  role: 'Maternal Grandmother', born: '1932', place: 'Galway, Ireland', living: false },
    { id: 'gp-2', name: 'Henry Marsh',    role: 'Maternal Grandfather', born: '1928', place: 'Dublin, Ireland', living: false },
    { id: 'gp-3', name: 'Iris Whitlow',   role: 'Paternal Grandmother', born: '1935', place: 'Memphis, USA',    living: true  },
    { id: 'gp-4', name: 'Walter Whitlow', role: 'Paternal Grandfather', born: '1929', place: 'Memphis, USA',    living: false },
  ],
  parents: [
    { id: 'p-1', name: 'Margaret Whitlow', role: 'Mother', born: '1958', place: 'Boston, USA',  living: true, parents: ['gp-1', 'gp-2'] },
    { id: 'p-2', name: 'David Whitlow',    role: 'Father', born: '1955', place: 'Memphis, USA', living: true, parents: ['gp-3', 'gp-4'] },
  ],
  parentSiblings: [
    { id: 'as-1', name: 'Catherine Marsh',  role: 'Aunt (M)',  born: '1960' },
    { id: 'as-2', name: 'Robert Marsh',     role: 'Uncle (M)', born: '1962' },
    { id: 'as-3', name: 'Eleanor Whitlow',  role: 'Aunt (P)',  born: '1957' },
  ],
  self: { id: 'self', name: 'You', role: 'You', born: '1985', place: 'San Francisco, USA' },
  selfSiblings: [
    { id: 'sib-1', name: 'James Whitlow', role: 'Brother', born: '1983' },
    { id: 'sib-2', name: 'Sara Whitlow',  role: 'Sister',  born: '1988' },
  ],
  kids: [
    { id: 'k-1', name: 'Elias Whitlow', role: 'Son',      born: '2014' },
    { id: 'k-2', name: 'Maya Whitlow',  role: 'Daughter', born: '2017' },
  ],
};

// Constellation coordinates per generation band
const BANDS = [
  { y: 110, label: '1928 – 1935 · Industrial Era',    nodes: TREE.grandparents.map((p, i) => ({ ...p, x: 200 + i * 240 })) },
  { y: 340, label: '1955 – 1962 · Post-War',           nodes: [
      { ...TREE.parentSiblings[0], x: 220, faint: true },
      { ...TREE.parents[0],         x: 440 },
      { ...TREE.parents[1],         x: 720 },
      { ...TREE.parentSiblings[1],  x: 940, faint: true },
      { ...TREE.parentSiblings[2],  x: 1110, faint: true },
  ] },
  { y: 560, label: '1983 – 1988 · You',                 nodes: [
      { ...TREE.selfSiblings[0], x: 380, faint: true },
      { ...TREE.self,             x: 580, isSelf: true },
      { ...TREE.selfSiblings[1], x: 780, faint: true },
  ] },
  { y: 770, label: '2014 – present · Children',         nodes: [
      { ...TREE.kids[0], x: 480 },
      { ...TREE.kids[1], x: 680 },
  ] },
];

// Edges: parent → child links to draw as SVG filaments
const EDGES = [
  // grandparents → parents
  ['gp-1', 'p-1'], ['gp-2', 'p-1'],
  ['gp-3', 'p-2'], ['gp-4', 'p-2'],
  // parents → self + self siblings
  ['p-1', 'self'], ['p-2', 'self'],
  ['p-1', 'sib-1'], ['p-2', 'sib-1'],
  ['p-1', 'sib-2'], ['p-2', 'sib-2'],
  // self → kids
  ['self', 'k-1'], ['self', 'k-2'],
];

const STEPS = [
  {
    title: 'Five generations, one chain',
    body: 'My Roots organizes your family history as a constellation. Each person becomes a node; each parent–child link becomes a glowing filament that earns its place only when a source confirms it.',
  },
  {
    title: 'You sit at the center',
    body: 'Your row is the spine. Siblings drift on either side, kids below, parents and grandparents above. Aunts and uncles are nudged out a half step so the parent–child filaments stay clean.',
  },
  {
    title: 'Sources earn the lines',
    body: "TSOGN won't draw a parent-child line without at least one citable source — a birth certificate, immigration record, oral history note, or DNA match. Methodology hold #3: the evidence log is sovereign.",
  },
];

// ─── Build a flat lookup so edges can find x/y by id ───────────────
function buildIndex() {
  const idx = {};
  for (const band of BANDS) {
    for (const n of band.nodes) {
      idx[n.id] = { x: n.x, y: band.y, name: n.name };
    }
  }
  return idx;
}

// ─── Glowing ancestor orb ──────────────────────────────────────────
function AncestorNode({ node, y, idx, total }) {
  const [hover, setHover] = useState(false);
  const isSelf = node.isSelf;
  const isFaint = node.faint;
  const size = isSelf ? 78 : isFaint ? 50 : 64;
  const ringColor = isSelf ? '#D4A84A' : '#2F8A92';
  const initials = node.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.4 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, delay: 1.2 + idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="cursor-pointer"
      data-testid={`person-${node.id} roots-node`}
    >
      {/* Outer glow ring (cyan or gold) */}
      <circle
        cx={node.x}
        cy={y}
        r={size / 2 + 6}
        fill="none"
        stroke={ringColor}
        strokeWidth={1}
        opacity={isFaint ? 0.25 : 0.55}
        style={{ filter: `drop-shadow(0 0 12px ${ringColor}88)` }}
      />
      {isSelf && (
        <motion.circle
          cx={node.x}
          cy={y}
          r={size / 2 + 6}
          fill="none"
          stroke="#D4A84A"
          strokeWidth={1}
          animate={{ r: [size / 2 + 6, size / 2 + 22, size / 2 + 6], opacity: [0.6, 0, 0.6] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      {/* Inner orb */}
      <circle
        cx={node.x}
        cy={y}
        r={size / 2}
        fill="url(#orbGradient)"
        opacity={isFaint ? 0.7 : 1}
      />
      <circle
        cx={node.x}
        cy={y}
        r={size / 2}
        fill="none"
        stroke={ringColor}
        strokeWidth={isSelf ? 1.5 : 1}
        opacity={isFaint ? 0.4 : 0.8}
      />
      {/* Initials inside orb */}
      <text
        x={node.x}
        y={y + 5}
        textAnchor="middle"
        className="font-historical pointer-events-none select-none"
        fill={isSelf ? '#D4A84A' : '#FFFFFF'}
        opacity={isFaint ? 0.6 : 0.95}
        style={{ fontSize: isSelf ? 22 : isFaint ? 14 : 18 }}
      >
        {initials}
      </text>
      {/* Name label below */}
      <text
        x={node.x}
        y={y + size / 2 + 22}
        textAnchor="middle"
        className="font-historical pointer-events-none select-none"
        fill={isSelf ? '#D4A84A' : '#FFFFFF'}
        opacity={isFaint ? 0.55 : 0.85}
        style={{ fontSize: isSelf ? 17 : 14 }}
      >
        {node.name}
      </text>
      <text
        x={node.x}
        y={y + size / 2 + 40}
        textAnchor="middle"
        className="font-mono pointer-events-none select-none"
        fill="#FFFFFF"
        opacity={0.4}
        style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase' }}
      >
        {node.role}
      </text>
      {/* Hover tooltip */}
      <AnimatePresence>
        {hover && (
          <motion.foreignObject
            x={node.x + size / 2 + 14}
            y={y - 36}
            width={210}
            height={88}
            initial={{ opacity: 0, x: node.x + size / 2 + 4 }}
            animate={{ opacity: 1, x: node.x + size / 2 + 14 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ pointerEvents: 'none' }}
          >
            <div className="rounded-md border border-[#D4A84A]/40 bg-black/85 backdrop-blur-md px-3 py-2 shadow-[0_0_18px_rgba(212,168,74,0.18)]">
              <div className="font-mono text-[9px] tracking-widest uppercase text-[#D4A84A]">
                {node.role}
              </div>
              <div className="font-historical text-white text-sm leading-tight mt-0.5">
                {node.name}
              </div>
              <div className="flex items-center gap-1 font-mono text-[10px] text-white/60 mt-1">
                <Calendar size={9} className="text-[#2F8A92]/70" /> {node.born}
                {node.living === false && <span className="text-white/30">· d.</span>}
              </div>
              {node.place && (
                <div className="flex items-center gap-1 font-mono text-[10px] text-white/55 mt-0.5 truncate">
                  <MapPin size={9} className="text-[#2F8A92]/70" /> {node.place}
                </div>
              )}
            </div>
          </motion.foreignObject>
        )}
      </AnimatePresence>
    </motion.g>
  );
}

// ─── Connecting filament ───────────────────────────────────────────
function Filament({ from, to, idx }) {
  if (!from || !to) return null;
  const midY = (from.y + to.y) / 2;
  const d = `M ${from.x} ${from.y + 32} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y - 32}`;
  return (
    <motion.path
      d={d}
      stroke="url(#filamentGradient)"
      strokeWidth={1.1}
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.55 }}
      transition={{ duration: 1.6, delay: 0.4 + idx * 0.05, ease: 'easeInOut' }}
      style={{ filter: 'drop-shadow(0 0 4px rgba(212,168,74,0.6))' }}
    />
  );
}

// ─── HUD walkthrough strip (consistent with profile) ──────────────
function HUDWalkthrough({ stepIdx, setStepIdx, total, step, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-md backdrop-blur-xl"
      data-testid="my-roots-walkthrough"
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#2F8A92] shadow-[0_0_12px_2px_rgba(47,138,146,0.55)]" />
      <div className="relative pl-6 pr-5 py-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkles size={11} className="text-[#2F8A92]" />
            <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#2F8A92]/85">
              Walkthrough · {stepIdx + 1} / {total}
            </span>
            <div className="flex gap-1.5 ml-2">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1 w-5 rounded-full ${i <= stepIdx ? 'bg-[#D4A84A]' : 'bg-white/15'}`}
                />
              ))}
            </div>
          </div>
          <div className="font-historical text-white text-lg md:text-xl mb-1">{step.title}</div>
          <p className="font-mono text-xs md:text-sm text-zinc-300/85 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          {stepIdx < total - 1 ? (
            <button
              onClick={() => setStepIdx(stepIdx + 1)}
              className="inline-flex items-center gap-1 font-mono text-xs text-[#2F8A92] hover:text-[#D4A84A] transition-colors"
              data-testid="walkthrough-next"
            >
              Next <ArrowRight size={12} />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="font-mono text-xs text-[#D4A84A] hover:text-white transition-colors"
              data-testid="walkthrough-close"
            >
              Got it
            </button>
          )}
          <button
            onClick={onClose}
            className="font-mono text-[10px] text-white/40 hover:text-white/70 transition-colors"
            data-testid="walkthrough-dismiss"
          >
            Skip walkthrough
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────
export default function MyRootsDemo() {
  const [stepIdx, setStepIdx] = useState(0);
  const [walkthroughOpen, setWalkthroughOpen] = useState(true);
  const [hint, setHint] = useState(true);
  const containerRef = useRef(null);
  const idx = buildIndex();

  // Auto-hide pan hint after a moment
  useEffect(() => {
    const t = setTimeout(() => setHint(false), 4500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="relative min-h-screen bg-[#0A0A0A] text-white overflow-hidden"
      data-testid="roots-page"
    >
      {/* Constellation background — faint dot grid + radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 1100px 500px at 50% -10%, rgba(212,168,74,0.10), transparent 60%),' +
            'radial-gradient(ellipse 800px 600px at 50% 110%, rgba(47,138,146,0.08), transparent 65%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(212,168,74,0.6) 1px, transparent 1.2px)',
          backgroundSize: '34px 34px',
        }}
      />

      <div className="relative px-4 md:px-10 lg:px-16 pt-10 md:pt-14 pb-8 max-w-7xl mx-auto" data-testid="my-roots-demo">
        {/* Header */}
        <div className="mb-7 flex items-center gap-3 font-mono text-[10px] tracking-[0.32em] uppercase">
          <span className="text-[#D4A84A]">Demo Mode</span>
          <span className="text-white/25">·</span>
          <span className="text-white/45">sample tree, replace with your own</span>
          <span className="ml-2 inline-flex items-center gap-1.5 text-[#2F8A92]/70">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2F8A92] shadow-[0_0_8px_rgba(47,138,146,0.8)] animate-pulse" />
            Ancestral constellation
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="font-historical text-[#D4A84A] text-5xl md:text-7xl font-light tracking-tight leading-[0.95] mb-3"
          data-testid="my-roots-title"
        >
          My Roots
        </motion.h1>
        <p className="font-historical text-white/70 text-base md:text-lg leading-relaxed max-w-3xl mb-5">
          Your family chain — sourced like every other chain on TSOGN. Five generations are seeded
          below as a constellation. Hover any star to read the source.
        </p>

        <div className="flex flex-wrap gap-3 mb-7">
          <button
            data-testid="my-roots-add-member roots-add-member"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#D4A84A] border border-[#D4A84A]/55 bg-[#D4A84A]/[0.06] hover:bg-[#D4A84A] hover:text-black px-4 py-2.5 rounded-md transition-all shadow-[0_0_18px_rgba(212,168,74,0.15)]"
          >
            <Plus size={13} /> Add a family member
          </button>
          <button
            data-testid="my-roots-import roots-import-gedcom"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#2F8A92] border border-[#2F8A92]/45 bg-black hover:bg-[#2F8A92]/[0.08] px-4 py-2.5 rounded-md transition-all"
          >
            <Compass size={13} /> Import GEDCOM
          </button>
        </div>

        {walkthroughOpen && (
          <div className="mb-5">
            <HUDWalkthrough
              stepIdx={stepIdx}
              setStepIdx={setStepIdx}
              total={STEPS.length}
              step={STEPS[stepIdx]}
              onClose={() => setWalkthroughOpen(false)}
            />
          </div>
        )}
      </div>

      {/* ─── CONSTELLATION CANVAS ───────────────────────────────── */}
      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 md:px-10 lg:px-16 pb-14"
      >
        <div className="relative h-[860px] rounded-lg border border-white/8 overflow-hidden bg-black/30">
          {/* Pan hint */}
          <AnimatePresence>
            {hint && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute top-4 right-4 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/15 font-mono text-[10px] tracking-widest uppercase text-white/70"
              >
                <Move size={11} /> Drag to pan
              </motion.div>
            )}
          </AnimatePresence>

          {/* Era band watermarks (z-0) */}
          <div className="pointer-events-none absolute inset-0 z-0">
            {BANDS.map((b, i) => (
              <div
                key={i}
                className="absolute left-0 right-0 font-historical text-white/[0.04] whitespace-nowrap select-none"
                style={{ top: b.y - 60, fontSize: '5.5rem', letterSpacing: '-0.02em', paddingLeft: '4rem' }}
              >
                {b.label}
              </div>
            ))}
            {BANDS.map((b, i) => (
              <div
                key={`label-${i}`}
                className="absolute left-6 font-mono text-[10px] tracking-[0.32em] uppercase text-[#D4A84A]/55"
                style={{ top: b.y - 80 }}
                data-testid={
                  i === 0 ? 'gen-grandparents' :
                  i === 1 ? 'gen-parents' :
                  i === 2 ? 'gen-self' :
                  'gen-kids'
                }
              >
                {b.label}
              </div>
            ))}
          </div>

          {/* Draggable constellation surface */}
          <motion.div
            drag
            dragConstraints={{ left: -200, right: 100, top: -150, bottom: 100 }}
            dragElastic={0.18}
            dragMomentum={true}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            data-testid="roots-tree"
          >
            <svg
              viewBox="0 0 1300 880"
              className="w-full h-full"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="filamentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%"  stopColor="#D4A84A" stopOpacity="0.85" />
                  <stop offset="50%" stopColor="#2F8A92" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#D4A84A" stopOpacity="0.85" />
                </linearGradient>
                <radialGradient id="orbGradient" cx="35%" cy="30%" r="65%">
                  <stop offset="0%" stopColor="#1f1f1f" />
                  <stop offset="100%" stopColor="#050505" />
                </radialGradient>
              </defs>

              {/* Draw filaments first (z-back) */}
              {EDGES.map(([from, to], i) => (
                <Filament key={`${from}-${to}`} from={idx[from]} to={idx[to]} idx={i} />
              ))}

              {/* Render every node */}
              {BANDS.map((band) =>
                band.nodes.map((node, i) => (
                  <AncestorNode
                    key={node.id}
                    node={node}
                    y={band.y}
                    idx={i + band.y / 100}
                    total={band.nodes.length}
                  />
                ))
              )}

              {/* "Add a child" pseudo-node */}
              <motion.g
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 2.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid="my-roots-add-child"
                className="cursor-pointer"
              >
                <circle cx={870} cy={770} r={34} fill="none" stroke="#D4A84A" strokeWidth={1} strokeDasharray="3 4" opacity={0.55} />
                <text x={870} y={777} textAnchor="middle" className="font-mono select-none" fill="#D4A84A" opacity={0.75} style={{ fontSize: 22 }}>+</text>
                <text x={870} y={825} textAnchor="middle" className="font-mono select-none" fill="#D4A84A" opacity={0.55} style={{ fontSize: 9, letterSpacing: '0.25em', textTransform: 'uppercase' }}>Add child</text>
              </motion.g>
            </svg>
          </motion.div>
        </div>

        {/* Footer */}
        <footer
          className="border-t border-white/10 pt-7 mt-8 text-center"
          data-testid="my-roots-footer"
        >
          <p className="font-mono text-[11px] tracking-widest uppercase text-white/40 leading-relaxed">
            <Users size={12} className="inline-block mr-1.5 -mt-0.5 text-[#D4A84A]/70" />
            When you start your real tree, every filament between two people requires at least one source —
            birth certificate, immigration record, oral history, or DNA match.
          </p>
        </footer>
      </div>
    </div>
  );
}
